import { useRef, useState } from 'react';
import { Text, Tooltip, type TextProps } from '@cowbell/ui';

type ClampedTextProps = Readonly<
  Pick<TextProps, 'size' | 'fw' | 'c'> & {
    children: string;
    lineClamp: number;
  }
>;

/**
 * Text clamped to `lineClamp` lines that shows the whole of it in a tooltip,
 * but only when the clamp actually cut something off. Overflow is measured on
 * hover, so the check tracks the card's current width without a resize observer.
 */
export default function ClampedText({
  children,
  lineClamp,
  ...textProps
}: ClampedTextProps) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [clipped, setClipped] = useState(false);

  const measure = () => {
    const el = ref.current;
    if (el) setClipped(el.scrollHeight > el.clientHeight + 1);
  };

  return (
    <Tooltip
      label={children}
      disabled={!clipped}
      multiline
      // A multiline tooltip has no intrinsic width and collapses to a word per
      // line; size it to the text, capped at a readable measure.
      w="max-content"
      maw="24rem"
      withArrow
      openDelay={300}
    >
      <Text
        ref={ref}
        lineClamp={lineClamp}
        onMouseEnter={measure}
        onFocus={measure}
        {...textProps}
      >
        {children}
      </Text>
    </Tooltip>
  );
}
